<template>
  <div class="result-container">
    <div class="page-header">
      <img src="@/assets/images/head.jpg" alt="页头图片" class="header-image">
    </div>
    <div class="header">
      <h1>测评结果分析</h1>
      <div class="energy-chart">
        <h3>心理能量图</h3>
        <div class="result-image">
          <img src="@/assets/images/result-banner.png" alt="心理能量图" />
        </div>
        <p class="chart-desc">此图展示了您当前的心理能量状态分布</p>
      </div>
      <div class="user-info" v-if="userInfo.name">
        <p>姓名：{{ userInfo.name }}</p>
        <p>性别：{{ userInfo.gender === 'male' ? '男' : '女' }}</p>
        <p>年龄：{{ userInfo.age }}</p>
      </div>
    </div>

    <div class="score-summary">
      <div class="total-score">
        <h2>总分：{{ totalScore }}/120分</h2>
        <div class="grade">
          <span :class="gradeClass">{{ grade }}</span>
        </div>
      </div>
      <div class="grade-description">
        {{ gradeDescription }}
      </div>
    </div>

    <!-- 图表展示 -->
    <div class="charts-container">
      <div class="chart-box">
        <h3>各模块得分分布</h3>
        <div ref="barChart" class="chart"></div>
      </div>
      <div class="chart-box">
        <h3>得分占比分析</h3>
        <div ref="pieChart" class="chart"></div>
      </div>
    </div>

    <!-- 详细分析 -->
    <div class="analysis-section">
      <h3>详细分析</h3>
      <div v-for="(score, index) in scores" :key="index" class="module-analysis">
        <h4>{{ getModuleTitle(index) }}</h4>
        <div class="score-bar">
          <div class="score-progress" :style="{ width: (score/10*100) + '%', backgroundColor: getScoreColor(score) }"></div>
          <span class="score-text">{{ score }}/10分</span>
        </div>
        <p>{{ getModuleAnalysis(score, index) }}</p>
      </div>
    </div>

    <!-- 建议部分 -->
    <div class="suggestions">
      <h3>改进建议</h3>
      <ul>
        <li v-for="(suggestion, index) in getSuggestions()" :key="index">
          {{ suggestion }}
        </li>
      </ul>
    </div>

    <!-- 返回按钮 -->
    <div class="actions">
      <el-button type="primary" @click="$router.push('/')">重新测试</el-button>
    </div>

    <div class="footer">
      <p>版权为
        <a href="https://www.shenchu2.com" target="_blank">
          佛山市顺德区乐从镇神出兔没广告设计部
        </a>
        所有
      </p>
      <p>
        <a href="https://beian.miit.gov.cn" target="_blank">
          粤ICP备18095156号
        </a>
      </p>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'

export default {
  name: 'QuestionnaireResult',
  data() {
    return {
      scores: [],
      userInfo: {},
      barChart: null,
      pieChart: null,
      moduleNames: [
        '家庭', '机遇', '环境', '行善积德',
        '学习', '价值', '世界观', '敬畏',
        '健康', '人际', '择业创业', '婚姻'
      ]
    }
  },
  computed: {
    totalScore() {
      return this.scores.reduce((sum, score) => sum + score, 0)
    },
    grade() {
      const score = this.totalScore
      if (score >= 108) return '优秀'
      if (score >= 96) return '良好'
      if (score >= 82) return '一般'
      if (score >= 72) return '及格'
      return '不及格'
    },
    gradeClass() {
      return {
        'grade-excellent': this.totalScore >= 108,
        'grade-good': this.totalScore >= 96 && this.totalScore < 108,
        'grade-average': this.totalScore >= 82 && this.totalScore < 96,
        'grade-pass': this.totalScore >= 72 && this.totalScore < 82,
        'grade-fail': this.totalScore < 72
      }
    },
    gradeDescription() {
      const descriptions = {
        '优秀': '您的总分达到108分以上，展现出了优秀的心理素质和生活态度，继续保持！',
        '良好': '您的总分在96-107分之间，整体表现良好，仍有提升空间。',
        '一般': '您的总分在82-95分之间，表现处于中等水平，需要在某些方面加强。',
        '及格': '您的总分在72-81分之间，刚刚及格，建议认真关注需要改进的方面。',
        '不及格': '您的总分在72分以下，未达到及格线，建议及时调整并寻求帮助。'
      }
      return descriptions[this.grade]
    }
  },
  mounted() {
    this.initData()
    this.initCharts()
    window.addEventListener('resize', this.resizeCharts)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeCharts)
    this.barChart?.dispose()
    this.pieChart?.dispose()
  },
  methods: {
    initData() {
      const { scores, userInfo } = this.$route.query
      this.scores = JSON.parse(scores)
      this.userInfo = JSON.parse(userInfo)
    },
    initCharts() {
      this.initBarChart()
      this.initPieChart()
    },
    initBarChart() {
      this.barChart = echarts.init(this.$refs.barChart)
      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: this.moduleNames,
          axisLabel: {
            interval: 0,
            rotate: 45
          }
        },
        yAxis: {
          type: 'value',
          max: 10
        },
        series: [{
          data: this.scores,
          type: 'bar',
          itemStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: '#83bff6' },         // 浅蓝色渐变开始
              { offset: 0.5, color: '#188df0' },       // 中间色
              { offset: 1, color: '#188df0' }          // 渐变结束
            ]),
            borderRadius: [5, 5, 0, 0]                 // 柱状图顶部圆角
          },
          emphasis: {                                  // 鼠标悬停效果
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: '#66a6ff' },
                { offset: 0.7, color: '#1677ff' },
                { offset: 1, color: '#1677ff' }
              ])
            }
          }
        }]
      }
      this.barChart.setOption(option)
    },
    initPieChart() {
      this.pieChart = echarts.init(this.$refs.pieChart)
      const option = {
        tooltip: {
          trigger: 'item',
          formatter: '{b}: {c}分\n({d}%)'
        },
        legend: {
          type: 'scroll',
          orient: 'horizontal',
          bottom: 0,
          formatter: '{name}'
        },
        media: [
          {
            query: {
              maxWidth: 768
            },
            option: {
              legend: {
                right: 'center',
                top: 'bottom',
                orient: 'horizontal',
                pageButtonPosition: 'end',
                type: 'scroll',
                width: '100%'
              },
              series: [{
                center: ['50%', '50%'],
                radius: ['30%', '45%'],
                label: {
                  show: true,
                  position: 'outside',
                  formatter: '{b}: {c}分\n({d}%)',
                  color: '#333',
                  fontSize: 9
                }
              }]
            }
          }
        ],
        series: [{
          type: 'pie',
          radius: ['30%', '45%'],
          center: ['50%', '50%'],
          avoidLabelOverlap: false,
          label: {
            show: true,
            position: 'outside',
            formatter: '{b}: {c}分\n({d}%)',
            color: '#333',
            fontSize: 11
          },
          labelLine: {
            show: true
          },
          data: this.moduleNames.map((name, index) => ({
            name,
            value: this.scores[index]
          }))
        }]
      }
      this.pieChart.setOption(option)
    },
    resizeCharts() {
      this.barChart?.resize()
      this.pieChart?.resize()
    },
    getModuleTitle(index) {
      return `${index + 1}. ${this.moduleNames[index]}`
    },
    getScoreColor(score) {
      return '#409EFF'    // 只在进度条使用浅蓝色
    },
    getModuleAnalysis(score, index) {
      const level = score >= 9 ? '优秀' : score >= 8 ? '良好' : score >= 7 ? '一般' : '需要改进'
      return `在${this.moduleNames[index]}方面的表现${level}，得分${score}分。`
    },
    getSuggestions() {
      const suggestions = []
      this.scores.forEach((score, index) => {
        if (score < 7) {
          suggestions.push(`建议加强${this.moduleNames[index]}方面的学习和提升。`)
        }
      })
      return suggestions.length ? suggestions : ['您的整体表现不错，建议继续保持！']
    },
    printResult() {
      window.print()
    }
  }
}
</script>

<style scoped>
.result-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  background: #fff;
  min-height: 100vh;
  box-shadow: 0 0 20px rgba(0,0,0,0.05);
}

.header {
  text-align: center;
  margin-bottom: 30px;
  padding: 30px 0;
  background: linear-gradient(135deg, #f6f8ff 0%, #ffffff 100%);
  border-radius: 12px;
}

.header h1 {
  font-size: 28px;
  color: #2c3e50;
  margin-bottom: 20px;
  font-weight: 600;
  letter-spacing: 1px;
}

.user-info {
  display: flex;
  justify-content: center;
  gap: 20px;
  background: rgba(255,255,255,0.9);
  padding: 15px 30px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  display: inline-flex;
}

.score-summary {
  text-align: center;
  margin-bottom: 40px;
  padding: 30px;
  background: linear-gradient(135deg, #f8f9ff 0%, #f0f2f5 100%);
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.05);
}

.total-score {
  font-size: 32px;
  margin-bottom: 20px;
  color: #2c3e50;
  font-weight: 600;
}

.grade {
  margin: 15px 0;
}

.grade span {
  padding: 8px 25px;
  border-radius: 25px;
  font-weight: bold;
  box-shadow: 0 3px 8px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
}

.grade-excellent { background: linear-gradient(45deg, #FF1493, #FF69B4); color: white; }
.grade-good { background: linear-gradient(45deg, #FF4500, #FFA500); color: white; }
.grade-average { background: linear-gradient(45deg, #FFD700, #FFC125); color: white; }
.grade-pass { background: linear-gradient(45deg, #32CD32, #98FB98); color: white; }
.grade-fail { background: linear-gradient(45deg, #1E90FF, #87CEEB); color: white; }

.grade-description {
  color: #666;
  font-size: 16px;
}

.charts-container {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 40px;
}

.chart-box {
  flex: 1;
  min-width: 300px;
  background: #fff;
  padding: 25px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  transition: transform 0.3s ease;
}

.chart-box:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 25px rgba(255,20,147,0.1);
}

.chart {
  height: 400px;
  margin-top: 20px;
}

.analysis-section {
  margin-bottom: 40px;
}

.module-analysis {
  margin: 15px 0;
  padding: 20px;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%);
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
  transition: transform 0.3s ease;
}

.module-analysis:hover {
  transform: translateX(5px);
  box-shadow: 0 4px 15px rgba(255,20,147,0.1);
}

.score-bar {
  height: 20px;
  background: #eee;
  border-radius: 10px;
  margin: 15px 0;
  position: relative;
  overflow: hidden;
  box-shadow: inset 0 1px 3px rgba(0,0,0,0.1);
}

.score-progress {
  height: 100%;
  transition: all 0.4s ease;
  background-image: linear-gradient(45deg, rgba(255,255,255,.25) 25%, transparent 25%, transparent 50%, rgba(255,255,255,.25) 50%, rgba(255,255,255,.25) 75%, transparent 75%, transparent);
  background-size: 40px 40px;
  animation: progress-bar-stripes 2s linear infinite;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

@keyframes progress-bar-stripes {
  from { background-position: 40px 0; }
  to { background-position: 0 0; }
}

.score-text {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #333;
  font-size: 12px;
}

.suggestions {
  margin-bottom: 40px;
  padding: 25px;
  background: linear-gradient(135deg, #f6f8ff 0%, #ffffff 100%);
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.05);
}

.suggestions ul {
  padding-left: 20px;
}

.suggestions li {
  margin: 12px 0;
  color: #666;
  position: relative;
  padding-left: 20px;
}

.suggestions li::before {
  content: "•";
  position: absolute;
  left: 0;
  color: #FF1493;
  font-size: 18px;
  line-height: 1;
}

.actions {
  text-align: center;
  margin-top: 40px;
  padding: 20px 0;
}

.actions .el-button {
  padding: 12px 30px;
  font-size: 16px;
  border-radius: 25px;
  transition: all 0.3s ease;
}

.actions .el-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.actions .el-button.el-button--success {
  background: linear-gradient(45deg, #32CD32, #98FB98);
  border: none;
  color: white;
}

.actions .el-button.el-button--primary {
  background: #409EFF;
  border: none;
  color: white;
}

h3 {
  color: #2c3e50;
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 2px solid #eef2f7;
}

h4 {
  color: #34495e;
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 15px;
}

@media print {
  .actions {
    display: none;
  }
}

@media screen and (max-width: 768px) {
  .result-container {
    padding: 15px;
  }

  .chart-box {
    flex: 100%;
  }
  
  .chart {
    height: 300px;
  }

  .user-info {
    flex-direction: column;
    gap: 10px;
  }

  .grade span {
    padding: 6px 20px;
    font-size: 14px;
  }
}

.result-image {
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.result-image img {
  width: 100%;
  height: auto;
  display: block;
}

.energy-chart {
  text-align: center;
  margin: 30px auto;
  max-width: 600px;
}

.energy-chart h3 {
  color: #2c3e50;
  font-size: 20px;
  margin-bottom: 15px;
  border: none;
}

.chart-desc {
  color: #666;
  font-size: 14px;
  margin-top: 10px;
}

.footer {
  text-align: center;
  padding: 20px 0;
  margin-top: 40px;
  border-top: 1px solid #eee;
  color: #666;
  font-size: 14px;
}

.footer p {
  margin: 5px 0;
}

.footer a {
  color: #666;
  text-decoration: none;
  transition: color 0.3s;
}

.footer a:hover {
  color: #409EFF;
}

.page-header {
  width: 100%;
  max-width: 1000px;
  margin: 0 auto 20px;
  text-align: center;
}

.header-image {
  width: auto;
  height: auto;
  max-height: 250px;
  max-width: 100%;
  display: block;
  object-fit: contain;
  margin: 0 auto;
}
</style> 